import { FF_ENABLE_AUTO_PROMOTION_CODE } from '@/constants/featureFlags';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/** Default promocode applied when an auto-promotion campaign is enabled */
export const AUTO_PROMO_DEFAULT_CODE = 'CHOOSEYOU-STAND';

type UseDiscountCodeCampaignOptions = {
  /**
   * Called with {@link AUTO_PROMO_DEFAULT_CODE} when the campaign is active and the URL has no promocode.
   * Should update applied code and input (e.g. trigger validation for the default).
   */
  onSeedDefaultCode: (code: string) => void;
};

/**
 * Feature-flagged auto-promotion: seeds discount state from the default campaign code when appropriate.
 * Auto-promotions are campaigns run by marketing 1-2 times a year so the feature flag toggle is permanent
 *
 * @param options - Campaign options (callback to seed the default code when the URL has no promocode)
 */
export function useDiscountCodeCampaign(options: UseDiscountCodeCampaignOptions) {
  const { onSeedDefaultCode } = options;
  const { flags } = useFeatureFlags();
  const isAutoPromotionEnabled = flags[FF_ENABLE_AUTO_PROMOTION_CODE];
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!isAutoPromotionEnabled) {
      return;
    }
    if (searchParams.get('promocode')) {
      return;
    }
    onSeedDefaultCode(AUTO_PROMO_DEFAULT_CODE);
  }, [isAutoPromotionEnabled, searchParams, onSeedDefaultCode]);
}
